import { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { SelectField } from "../../../core/select-field.component";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import { formatDate } from "@progress/kendo-intl";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { API_END_POINT } from "../../../constants";
import { getDataWithToken, getEndPoint } from "../../../Utils/network.client";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { PDFExport } from "@progress/kendo-react-pdf";
import { useGetSessions } from "../../../apiclient/master/useGetSessionApi";
import { useGetCompanyDetails } from "../../../apiclient/master/useGetCompanydetailsApi";

export const CashCollectionReport: React.FC = () => {
  const [sessionData, setSessionData] = useState<
    { value: string; label: string }[]
  >([]);
  const { data: fetchedSession } = useGetSessions();

  const excelExportRef = useRef<ExcelExport | null>(null);
  const pdfExportRef = useRef<PDFExport | null>(null);

  const { data: companyDetails } = useGetCompanyDetails();
  const companyName = companyDetails?.[0]?.companyName || "Company Name";

  const [customerType, setCustomerType] = useState(1);

  const exportToExcel = () => {
    if (excelExportRef.current) {
      const options = excelExportRef.current.workbookOptions();

      // Add a custom title row and company name
      options.sheets[0].rows.unshift(
        {
          cells: [
            {
              value: companyName,
              colSpan: productColumns.length + 2, // merge across all columns
              fontSize: 16,
              bold: true,
              textAlign: "center",
              background: "#D9E1F2",
            },
          ],
        },
        {
          cells: [
            {
              value: `Cash Collection CheckList For - ${fromDate2}`,
              colSpan: productColumns.length + 2,
              fontSize: 14,
              bold: true,
              textAlign: "center",
              background: "#FCE4D6",
            },
          ],
        },
        {
          cells: [],
        } // Empty row for spacing
      );

      excelExportRef.current.save(options);
    }
  };

  const exportToPDF = () => {
    if (pdfExportRef.current) {
      pdfExportRef.current.save();
    }
  };

  useEffect(() => {
    if (fetchedSession) {
      const mappedSession = fetchedSession.map((session: any) => ({
        value: session.sessionId,
        label: session.name,
      }));
      setSessionData(mappedSession);
    }
  }, [fetchedSession]);

  const [fromDate1, setFromDate] = useState<Date>(
    new Date(dayjs().add(0, "months").format("MM/DD/YYYY"))
  );
  const [fromDate2, setFromDate2] = useState<string>("");
  const [gridData, setGridData] = useState([]);
  const [columns, setColumns] = useState([]);

  const initialValues = {
    sessionId: "",
    fromDate: fromDate1,
  };

  const [sessionid, setSessionId] = useState([]);

  const handleSessionChange = (value: any) => {
    setSessionId(value);
    console.log("session id is:", sessionid);
  };

  const handleDateChange = (value: any) => {
    const day = value.getDate().toString().padStart(2, "0");
    const month = value
      .toLocaleString("default", { month: "short" })
      .toUpperCase(); // Get short month (e.g., JAN)
    const year = value.getFullYear();

    const formattedDate = new Date(`${year}-${month}-${day}`);
    setFromDate(formattedDate);

    const formattedDate2 = dayjs(value).format("ddd MMM DD YYYY");
    console.log(formattedDate2);
    setFromDate2(formattedDate2);
  };

  useEffect(() => {
    if (fromDate1) {
      const formattedDate2 = dayjs(fromDate1).format("ddd MMM DD YYYY");
      console.log("Formatted Date:", formattedDate2);
      setFromDate2(formattedDate2);
    }
  }, [fromDate1]);

  const handlecashcollectionsummary = async () => {
    try {
      const apiUrl = `${getEndPoint(
        API_END_POINT.REPORT
      )}/api/DailyReportsService/GetCashCollectionCheckList/${fromDate2}`;

      const response = await getDataWithToken<any>(apiUrl);

      const getData = response?.data || [];
      setGridData(getData);
      console.log("getData", getData);
    } catch (error) {
      console.error("Error fetching cash collection summary:", error);
    }
  };

  const productColumns = [
    ...new Set(gridData.map((item) => item.productShortName)),
  ];

  const groupedData: any[] = [];
  const groupedMap: { [key: string]: any } = {};

  gridData.forEach((item) => {
    const key = `${item.dpCode} - ${item.dpName}`;
    if (!groupedMap[key]) {
      groupedMap[key] = {
        dp: key,
        totalAmount: 0,
      };
      productColumns.forEach((p) => (groupedMap[key][p] = "-"));
    }
    groupedMap[key][item.productShortName] = item.liter;
    groupedMap[key].totalAmount += item.totalAmount;
  });

  for (const value of Object.values(groupedMap)) {
    groupedData.push(value);
  }

  // Calculate distinct totalAmount sum
  /*   const distinctAmounts = Array.from(new Set(gridData.map(item => item.totalAmount)));
      const grandTotal = distinctAmounts.reduce((sum, val) => sum + val, 0); */

  const dpCodeTotals = new Map<string, Set<number>>();

  // Group unique totalAmounts per dpCode
  gridData.forEach((item) => {
    if (!dpCodeTotals.has(item.dpCode)) {
      dpCodeTotals.set(item.dpCode, new Set());
    }
    dpCodeTotals.get(item.dpCode)?.add(item.totalAmount);
  });

  const dpCodeSums: { [dpCode: string]: number } = {};

  dpCodeTotals.forEach((amounts, dpCode) => {
    dpCodeSums[dpCode] = Array.from(amounts).reduce((sum, amt) => sum + amt, 0);
  });

  console.log("Sum per dpCode:", dpCodeSums);

  const hasRecords = gridData.length > 0;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        console.log("Form submitted with values: ", values);
      }}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <div
            className="m-lg-3 mt-lg-1 m-4 card p-3 border-0 m-3 sw-shadow radius-15"
            style={{ minHeight: "500px" }}
          >
            <div className="top-sec-align row">
              <center>
                <h4 className="text-normal title-web mb-0 report-title-color">
                  Cash Collection CheckList Report
                </h4>
              </center>
            </div>
            <div className="fade_rule"></div>
            <div className="d-flex justify-content-between mt-4 pt-3 pb-3">
              <div className="d-flex">
                <div className="me-2">
                  <label> Select Date</label>
                  <DatePicker
                    selectsMultiple={true}
                    openToDate={new Date()}
                    className="form-control ms-auto search-right-align calendarBg"
                    value={formatDate(fromDate1, "dd/MM/yyyy")}
                    placeholderText="To"
                    dateFormat={"dd/MM/yyyy"}
                    onChange={([date]: any) => {
                      handleDateChange(date);
                    }}
                  />
                </div>

                <div>
                  <label></label>
                  <button
                    className="btn-primary btn search-btns mt-4"
                    type="submit"
                    onClick={() => {
                      handlecashcollectionsummary();
                    }}
                  >
                    <svg width="17" height="17" viewBox="0 0 17.5 17.5">
                      <path
                        id="Path_magnify"
                        data-name="Path / magnify"
                        d="M9.5,3a6.5,6.5,0,0,1,4.94,10.73l.27.27h.79l5,5L19,20.5l-5-5v-.79l-.27-.27A6.5,6.5,0,1,1,9.5,3m0,2A4.5,4.5,0,1,0,14,9.5,4.481,4.481,0,0,0,9.5,5Z"
                        transform="translate(-3 -3)"
                        fill="#fff"
                      />
                    </svg>
                  </button>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    title="Export Excel"
                    className="export-button ms-2"
                    onClick={exportToExcel}
                    disabled={!hasRecords}
                    style={{ marginTop: "23px", marginBottom: "1px" }}
                  >
                    Export to Excel
                  </button>
                  <button
                    title="Export Excel"
                    className="export-button ms-2"
                    onClick={exportToPDF}
                    disabled={!hasRecords}
                    style={{ marginTop: "23px", marginBottom: "1px" }}
                  >
                    Export to PDF
                  </button>
                </div>
              </div>
            </div>
            <div>
              <PDFExport
                ref={pdfExportRef}
                fileName="Cash Collection CheckList.pdf"
              >
                <div
                  style={{
                    padding: "30px",
                    backgroundColor: "#f9f9f9",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  <div style={{ padding: "10px", backgroundColor: "#fff" }}>
                    <div style={{ textAlign: "center", marginBottom: "5px" }}>
                      <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                        {companyDetails?.[0]?.companyName || "Company Name"}
                      </span>
                    </div>
                  </div>
                  <h4
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "#003366",
                    }}
                  >
                    Cash Collection CheckList For{" "}
                    <span
                      style={{
                        fontSize: "18px",
                        marginLeft: "10px",
                        color: "#555",
                      }}
                    >
                      {" "}
                      - {fromDate2}
                    </span>
                  </h4>
                  {gridData.length > 0 ? (
                    <Grid data={groupedData} resizable>
                      <GridColumn field="dp" title="Booth" width="200px" />
                      {productColumns.map((product) => {
                        const totalLitres = groupedData.reduce((sum, item) => {
                          const val = parseFloat(item[product]);
                          return sum + (isNaN(val) ? 0 : val);
                        }, 0);

                        return (
                          <GridColumn
                            key={product}
                            field={product}
                            title={product}
                            footerCell={() => (
                              <td style={{ fontWeight: "bold" }}>
                                {totalLitres.toFixed(2)}
                              </td>
                            )}
                          />
                        );
                      })}
                      <GridColumn field="totalAmount" title="Total Amount" />
                    </Grid>
                  ) : (
                    <p>No data Found</p>
                  )}
                </div>
              </PDFExport>

              <ExcelExport
                data={groupedData}
                ref={excelExportRef}
                fileName="Cash Collection Summary.xlsx"
              >
                {groupedData.length > 0 &&
                  Object.keys(groupedData[0])
                    .filter((field) => field !== "totalAmount")
                    .map((field, index) => (
                      <ExcelExportColumn key={index} field={field} title={field} />
                    ))}
                <ExcelExportColumn
                  field="totalAmount"
                  title="Total Amount"
                  // Make sure totalAmount is last column
                />
              </ExcelExport>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

